// context/quiz_context.tsx
"use client";

import { isEmpty } from "es-toolkit/compat";
import { usePathname, useRouter } from "next/navigation";
import React, { createContext, useCallback, useEffect, useRef, useState } from "react";

import useQuizSubmission from "components/Quiz/hooks/useQuizSubmission";
import { checkQuizQuestionValid, getAdjustedPosition, QUESTION_TYPES, QUIZ_EVENT } from "components/Quiz/utils";
import {
  LATEST_ATTEMPTED_QUIZ_ANSWERS_LOCALSTORAGE_KEY,
  SELLER_ADDRESS_LOCALSTORAGE_KEY
} from "components/Tiles/constants";
import { useAnalytics } from "context/AnalyticsContext";
import { APP_LANGUAGES, APP_STRINGS_LABELS, getAppStringForLanguage } from "utils/language";
import { Quiz, QuizQuestionInputType, type QuizNextClickProps, type QuizQuestion } from "./quizTypes";
export interface QuizContextState {
  quizLanguage: APP_LANGUAGES;
  quizSlug: string;
  quizData: Quiz;
  quizBaseSlug: string;
  numberQuestions?: number;
  questions: QuizQuestion[];
  currentQuestion?: QuizQuestion;
  currentQuestionIndex?: number;
  updateCurrentQuestion: (questionId: string) => void;
  updateQuestion: ({
    questionId,
    updatedQuestion,
    submitQuiz,
    autoNextquestion
  }: {
    questionId: string;
    updatedQuestion: Partial<QuizQuestion>;
    submitQuiz?: boolean;
    autoNextquestion?: boolean;
  }) => void;
  onNext: (params?: QuizNextClickProps) => void;
  onBack: () => void;
  onClose: () => void;
  isSubmittingQuiz?: boolean;
  errorState?: {
    message: string;
    shouldRestart?: boolean;
  };
  handleRestartQuiz?: () => void;
  showAddressAdditionalFields?: boolean;
  resetAddressAdditionalInfoFields: () => void;
  phoneVerificationEnabled?: boolean;
  sliderLoaderEnabled?: boolean;
  quizSubmissionRedirectLink?: string;
}

// TODO: Enable Analytics later disabled for now
export const QuizContext = createContext<QuizContextState | null>(null);

const { Provider } = QuizContext;

const isQuizPhoneVerificationEnabled = (questions: QuizQuestion[]) =>
  questions.some((ques) => ques.type === QUESTION_TYPES.account && ques.enablePhoneVerification);

const isSliderLoaderEnabled = (questions: QuizQuestion[]) =>
  questions.some((ques) => ques.type === QUESTION_TYPES.account && ques.enableSliderLoader);

const QuizContextProvider = ({
  children,
  quizSlug,
  quizData
}: {
  children: React.ReactNode;
  quizSlug: string;
  quizData: Quiz;
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const questionsList = quizData.questions as QuizQuestion[];
  const quizLanguage = (quizData.language as APP_LANGUAGES) || APP_LANGUAGES.ENGLISH;
  const quizBaseSlug = pathname?.startsWith("/cuestionario") ? "cuestionario" : "quiz";
  const { handleSubmit } = useQuizSubmission(quizSlug, quizLanguage);
  const { track } = useAnalytics();
  const [questions, setQuestions] = useState<QuizQuestion[]>(questionsList);
  const [currentQuestion, setCurrentQuestion] = useState<QuizQuestion>();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isSubmittingQuiz, setIsSubmittingQuiz] = useState(false);
  const initAnswersPrefill = useRef(false);
  const [phoneVerificationEnabled, setPhoneVerificationEnabled] = useState(
    isQuizPhoneVerificationEnabled(questionsList)
  );
  const [sliderLoaderEnabled, setSliderLoaderEnabled] = useState(isSliderLoaderEnabled(questionsList));
  const [showAddressAdditionalFields, setShowAddressAdditionalFields] = useState(false);
  const [errorState, setErrorState] = useState<
    | {
        message: string;
        shouldRestart?: boolean;
      }
    | undefined
  >();
  const numberQuestions = questionsList.length;
  const [quizSubmissionRedirectLink, setQuizSubmissionRedirectLink] = useState<string | undefined>(undefined);

  useEffect(() => {
    setQuestions(questionsList);
  }, [questionsList]);

  useEffect(() => {
    setQuizSubmissionRedirectLink(undefined);
  }, [quizSlug]);

  useEffect(() => {
    if (!questionsList.length) return;
    setPhoneVerificationEnabled(isQuizPhoneVerificationEnabled(questionsList));
    setSliderLoaderEnabled(isSliderLoaderEnabled(questionsList));
  }, [questionsList]);

  const sendQuizAnswerSubmittedEvent = useCallback(
    (questionWithAnswer: QuizQuestion) => {
      track({
        eventName: QUIZ_EVENT.QUIZ_ANSWER_SUBMITTED,
        properties: {
          quiz_type: quizSlug,
          answer: questionWithAnswer.answer,
          question: questionWithAnswer.title
        }
      });
    },
    [track, quizSlug]
  );

  const resetAddressAdditionalInfoFields = useCallback(() => {
    setShowAddressAdditionalFields(false);
    const addressQuestion = questions.find((ques) => ques.type === QUESTION_TYPES.address);
    if (addressQuestion && addressQuestion?.additionalAddressInfoFields?.fields?.length) {
      setQuestions((prev) =>
        prev.map((ques) =>
          ques.id === addressQuestion.id
            ? { ...ques, additionalAddressInfoFields: { ...ques.additionalAddressInfoFields, fields: undefined } }
            : ques
        )
      );
    }
  }, [questions]);

  const onBack = useCallback(() => {
    resetAddressAdditionalInfoFields();
    router.back();
  }, [router, resetAddressAdditionalInfoFields]);

  const onClose = useCallback(() => {
    router.push(quizLanguage === APP_LANGUAGES.ENGLISH ? "/" : "/es");
  }, [router, quizLanguage]);

  const updateCurrentQuestion = useCallback(
    (questionId: string) => {
      const questionIndex = questions.findIndex((ques) => ques.id === questionId);
      let finalPosition = getAdjustedPosition(questionIndex.toString());
      if (questionIndex === -1) {
        finalPosition = 0;
      } else {
        if (!checkQuizQuestionValid(questions, questions[questionIndex])) {
          finalPosition = 0;
        }
      }
      // if the question  Index is different from validQuestion index, update route
      if (getAdjustedPosition(questionIndex.toString()) !== finalPosition) {
        router.push(`/${quizBaseSlug}/${quizSlug}/${questions[finalPosition].id}`);
      }
      setCurrentQuestion(questions[finalPosition]);
      setCurrentQuestionIndex(finalPosition);
    },
    [questions, router, quizSlug, quizBaseSlug]
  );

  const handleRestartQuiz = useCallback(() => {
    setErrorState(undefined);
    router.push(`/${quizBaseSlug}/${quizSlug}/${questionsList[0].id}`);
  }, [quizSlug, router, questionsList, quizBaseSlug]);

  const switchToNextQuestion = useCallback(
    (updatedQuestions?: QuizQuestion[]) => {
      window?.scrollTo(0, 0);

      const finalQuestions = updatedQuestions || questions;
      let nextQuestionIndex = currentQuestionIndex + 1;
      let nextQuestion = finalQuestions[nextQuestionIndex];
      while (nextQuestion) {
        if (checkQuizQuestionValid(finalQuestions, nextQuestion)) {
          break;
        }
        if (nextQuestionIndex === finalQuestions.length - 1) {
          break;
        }
        nextQuestionIndex++;
        nextQuestion = finalQuestions[nextQuestionIndex];
      }
      if (nextQuestion?.skipLink?.slug && !checkQuizQuestionValid(finalQuestions, nextQuestion)) {
        const answersToSave = finalQuestions.map((ques) => ques.answer).slice(0, currentQuestionIndex);
        //save answers for quiz to localStorage from where it skipped to this check
        localStorage.setItem(
          LATEST_ATTEMPTED_QUIZ_ANSWERS_LOCALSTORAGE_KEY,
          JSON.stringify({ [quizSlug]: answersToSave })
        );
        router.replace(`/${nextQuestion?.skipLink?.slug}`);
      } else {
        router.push(`/${quizBaseSlug}/${quizSlug}/${nextQuestion.id}`);
      }
    },
    [currentQuestionIndex, router, quizSlug, questions, quizBaseSlug]
  );

  const updateQuestion = useCallback(
    async ({
      questionId,
      updatedQuestion,
      submitQuiz,
      autoNextquestion
    }: {
      questionId: string;
      updatedQuestion: Partial<QuizQuestion>;
      submitQuiz?: boolean;
      autoNextquestion?: boolean;
    }) => {
      const questionToUpdate = questions.find((questionItem) => questionItem.id === questionId);
      if (questionToUpdate) {
        setCurrentQuestion({ ...questionToUpdate, ...updatedQuestion });
      }
      let savedAnswers: any;

      if (typeof window !== "undefined") {
        savedAnswers = localStorage.getItem(LATEST_ATTEMPTED_QUIZ_ANSWERS_LOCALSTORAGE_KEY);
      }

      if (savedAnswers) {
        const parsedAnswers = JSON.parse(savedAnswers);
        savedAnswers = parsedAnswers[quizSlug];
        localStorage.removeItem(LATEST_ATTEMPTED_QUIZ_ANSWERS_LOCALSTORAGE_KEY);
      }
      const updatedQuestions = questions.map((questionItem, index) => {
        const questionAnswer = savedAnswers?.[index];
        if (questionItem.id === questionId) {
          return {
            ...questionItem,
            ...updatedQuestion,
            answer: questionAnswer || updatedQuestion.answer || questionItem.answer
          };
        }
        return { ...questionItem, answer: questionAnswer || questionItem.answer };
      });
      setQuestions(updatedQuestions);

      if (
        questionToUpdate?.type === QUESTION_TYPES.address &&
        questionToUpdate?.additionalAddressInfoFields?.enabled &&
        !isEmpty(updatedQuestion?.answer) &&
        !showAddressAdditionalFields &&
        questionToUpdate.isAutoNextSlide
      ) {
        setShowAddressAdditionalFields(true);
        return;
      }

      if (questionToUpdate?.isAutoNextSlide && !questionToUpdate?.allowMultiAnswers) {
        sendQuizAnswerSubmittedEvent({
          ...questionToUpdate,
          ...updatedQuestion
        });
        if (questionToUpdate?.isRequired && isEmpty(updatedQuestion.answer)) {
          return;
        }
        switchToNextQuestion(updatedQuestions);
        return;
      }
      if (submitQuiz) {
        try {
          setIsSubmittingQuiz(true);
          const submitResponse = await handleSubmit(updatedQuestions, !phoneVerificationEnabled);

          if (submitResponse?.error) {
            if (submitResponse?.error === "Submission invalid") {
              setErrorState({
                message: "Some answers were missing or invalid, please restart the quiz and try again.",
                shouldRestart: true
              });
            }
            setIsSubmittingQuiz(false);
          } else {
            if (submitResponse?.redirectTo) {
              setQuizSubmissionRedirectLink(submitResponse.redirectTo);
            }

            if (phoneVerificationEnabled) {
              router.push(
                `/${quizBaseSlug}/${quizSlug}/${getAppStringForLanguage(APP_STRINGS_LABELS.PHONE_VERIFY_SEGMENT, quizLanguage)}`
              );
            }
          }
        } catch (error) {
          setIsSubmittingQuiz(false);
        }
      }

      if (autoNextquestion) {
        switchToNextQuestion(updatedQuestions);
      }
    },
    [
      handleSubmit,
      questions,
      switchToNextQuestion,
      showAddressAdditionalFields,
      sendQuizAnswerSubmittedEvent,
      phoneVerificationEnabled,
      quizSlug,
      quizBaseSlug,
      router,
      quizLanguage
    ]
  );

  useEffect(() => {
    if (typeof window !== "undefined" && questions?.length && !initAnswersPrefill.current) {
      const addressQuestion = questions.find((questionItem) => questionItem.type === QUESTION_TYPES.address);
      const savedAddress = localStorage.getItem(SELLER_ADDRESS_LOCALSTORAGE_KEY);
      initAnswersPrefill.current = true;
      if (addressQuestion && !addressQuestion?.answer && savedAddress) {
        const parsedAddress = JSON.parse(savedAddress);
        updateQuestion({ questionId: addressQuestion.id, updatedQuestion: { answer: parsedAddress } });
      } else {
        updateQuestion({ questionId: questions[0].id, updatedQuestion: {} });
      }
    }
  }, [questions, updateQuestion]);

  const onNext = useCallback(async () => {
    if (
      currentQuestion?.isRequired &&
      (currentQuestion.type === QUESTION_TYPES.input && currentQuestion.inputType === QuizQuestionInputType.Slider
        ? currentQuestion?.answer === undefined || currentQuestion?.answer === null
        : isEmpty(currentQuestion?.answer))
    ) {
      return;
    }
    if (
      currentQuestion?.type === QUESTION_TYPES.address &&
      currentQuestion?.additionalAddressInfoFields?.enabled &&
      !showAddressAdditionalFields
    ) {
      setShowAddressAdditionalFields(true);
      return;
    }
    if (currentQuestion) {
      sendQuizAnswerSubmittedEvent(currentQuestion);
    }
    switchToNextQuestion();
  }, [switchToNextQuestion, currentQuestion, showAddressAdditionalFields, sendQuizAnswerSubmittedEvent]);

  useEffect(() => {
    if (currentQuestionIndex === 0) {
      track({
        eventName: QUIZ_EVENT.QUIZ_STARTED,
        properties: { quizType: quizSlug }
      });
    }
  }, [currentQuestionIndex, track, quizSlug]);

  return (
    <Provider
      value={{
        quizData,
        questions,
        numberQuestions,
        currentQuestion,
        updateCurrentQuestion,
        updateQuestion,
        onNext,
        onBack,
        currentQuestionIndex,
        onClose,
        isSubmittingQuiz,
        quizSlug,
        quizBaseSlug,
        errorState,
        handleRestartQuiz,
        showAddressAdditionalFields,
        resetAddressAdditionalInfoFields,
        quizLanguage,
        phoneVerificationEnabled,
        sliderLoaderEnabled,
        quizSubmissionRedirectLink
      }}
    >
      {children}
    </Provider>
  );
};

export default QuizContextProvider;
